import React, { useEffect, useMemo } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { Button } from 'v3/components';
import Spinner from 'react-bootstrap/Spinner';
import { styles } from './styles';

export default function CloseLinkChecklistModal({
  open = false,
  onClose,
  onConfirm,
  checklistTitle = '',
  checklistId = null,
  responsesCount = 0,
  loading = false,
  loadingLabel = 'Encerrando...',
  isLinkClosed = false,
}) {
  const disableConfirm = loading || !!isLinkClosed;
  const displayTitle = useMemo(() => {
    const normalizedTitle = String(checklistTitle || 'Checklist').trim();
    if (!checklistId) return normalizedTitle;
    if (normalizedTitle.startsWith(`#${checklistId}`)) return normalizedTitle;
    return `#${checklistId} ${normalizedTitle}`;
  }, [checklistTitle, checklistId]);

  useEffect(() => {
    if (!open) return;

    function onEsc(e) {
      if (e.key === 'Escape' && !loading) {
        onClose && onClose();
      }
    }

    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose, loading]);

  if (!open) return null;

  const handleConfirm = () => {
    if (disableConfirm) return;
    onConfirm && onConfirm();
  };

  return (
    <div
      style={styles.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading && onClose) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-busy={loading ? 'true' : 'false'}
    >
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.titleRow}>
            <span style={styles.iconWarnWrap}>
              <WarningAmberOutlinedIcon style={{ fontSize: 18, color: '#B45309' }} />
            </span>
            Encerrar Checklist
          </div>

          <button
            type="button"
            style={{
              ...styles.closeBtn,
              ...(loading ? styles.closeBtnDisabled : null),
            }}
            onClick={() => !loading && onClose && onClose()}
            aria-label="Fechar"
            disabled={loading}
            title={loading ? 'Aguarde...' : 'Fechar'}
          >
            <CloseOutlinedIcon style={{ fontSize: 18, color: '#6B7280' }} />
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.description}>
            Tem certeza que deseja encerrar este checklist? O link de compartilhamento será
            desativado e nenhuma nova resposta poderá ser recebida.
          </div>

          <div style={styles.preview}>
            <div style={styles.previewTitle}>{displayTitle}</div>
            <div style={styles.previewSub}>
              {responsesCount} {responsesCount === 1 ? 'resposta recebida' : 'respostas recebidas'}
            </div>
          </div>

          {isLinkClosed && (
            <div style={styles.warnBox}>
              <HighlightOffOutlinedIcon
                style={{
                  fontSize: 18,
                  color: '#DC2626',
                  flexShrink: 0,
                }}
              />
              <div style={styles.warnText}>
                Este link está encerrado.
              </div>
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <Button variant="secondary" disabled={loading} onClick={onClose}>
            Cancelar
          </Button>

          <Button
            variant="danger"
            loading={loading}
            onClick={handleConfirm}
            disabled={disableConfirm}
            title={isLinkClosed ? 'Link encerrado' : 'Encerrar checklist'}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: 8 }}
                />
                {loadingLabel}
              </>
            ) : (
              <>
                <HighlightOffOutlinedIcon style={{ fontSize: 18, marginRight: 6 }} />
                Encerrar Checklist
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
